import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { 
  Heart, Bell, MessageSquare, Calendar, Phone, Video, BookOpen,
  Send, Clock, User, ArrowLeft, AlertTriangle, CheckCircle, 
  Cross, Users, FileText, ExternalLink
} from "lucide-react";
import { Link } from "react-router-dom";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function FamilyPortalDashboard() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState("overview");
  const [loading, setLoading] = useState(false);
  const [dashboard, setDashboard] = useState(null);
  const [resources, setResources] = useState({});
  const [updates, setUpdates] = useState([]);
  const [messages, setMessages] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [timeline, setTimeline] = useState([]);
  
  // Demo patient ID - in real app this comes from auth
  const patientId = "demo_patient_001";
  
  const [showMessageDialog, setShowMessageDialog] = useState(false);
  const [showServiceDialog, setShowServiceDialog] = useState(false);
  const [selectedService, setSelectedService] = useState("");
  
  const [newMessage, setNewMessage] = useState({
    from_name: "",
    from_relationship: "family",
    message: "",
    urgent: false
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [dashboardRes, resourcesRes, updatesRes, notificationsRes, timelineRes] = await Promise.all([
        fetch(`${API_URL}/api/family-portal/dashboard/${patientId}`),
        fetch(`${API_URL}/api/family-portal/resources`),
        fetch(`${API_URL}/api/family-portal/updates/${patientId}`),
        fetch(`${API_URL}/api/family-portal/notifications/${patientId}`),
        fetch(`${API_URL}/api/family-portal/care-timeline/${patientId}`)
      ]);
      
      const dashboardData = await dashboardRes.json();
      const resourcesData = await resourcesRes.json();
      const updatesData = await updatesRes.json();
      const notificationsData = await notificationsRes.json();
      const timelineData = await timelineRes.json();
      
      setDashboard(dashboardData);
      setResources(resourcesData.resources || {});
      setUpdates(updatesData.updates || []);
      setNotifications(notificationsData.notifications || []);
      setTimeline(timelineData.timeline || []);
    } catch (err) {
      console.error("Failed to load data:", err);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.from_name || !newMessage.message) {
      toast.error("Please fill in your name and message");
      return;
    }
    
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/family-portal/message-team`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          patient_id: patientId,
          ...newMessage
        })
      });
      
      const data = await res.json();
      
      if (data.success) {
        toast.success("Message sent to care team");
        setShowMessageDialog(false);
        setNewMessage({ from_name: "", from_relationship: "family", message: "", urgent: false });
        loadData();
      }
    } catch (err) {
      toast.error("Failed to send message");
    }
    setLoading(false);
  };

  const requestService = async (serviceType) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        patient_id: patientId,
        service_type: serviceType,
        requested_by: "Family Member"
      });
      
      const res = await fetch(`${API_URL}/api/family-portal/request-service?${params}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" }
      });
      
      const data = await res.json();
      
      if (data.success) {
        toast.success(data.message);
        setShowServiceDialog(false);
      } else {
        toast.error("Failed to request service");
      }
    } catch (err) {
      toast.error("Failed to request service");
    }
    setLoading(false);
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case "comfort_care": return <Heart className="w-5 h-5" />;
      case "grief_support": return <Users className="w-5 h-5" />;
      case "spiritual": return <Cross className="w-5 h-5" />;
      case "practical": return <FileText className="w-5 h-5" />;
      case "children": return <User className="w-5 h-5" />;
      case "legal_financial": return <FileText className="w-5 h-5" />;
      default: return <BookOpen className="w-5 h-5" />;
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case "comfort_care": return "bg-pink-100 text-pink-700 border-pink-200";
      case "grief_support": return "bg-purple-100 text-purple-700 border-purple-200";
      case "spiritual": return "bg-indigo-100 text-indigo-700 border-indigo-200";
      case "practical": return "bg-green-100 text-green-700 border-green-200";
      case "children": return "bg-blue-100 text-blue-700 border-blue-200";
      case "legal_financial": return "bg-amber-100 text-amber-700 border-amber-200";
      default: return "bg-slate-100 text-slate-700 border-slate-200";
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-teal-50 to-white p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Link to="/" className="text-slate-600 hover:text-slate-900">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="text-3xl font-bold text-teal-900">{t('familyPortal.title', 'Family Portal')}</h1>
              <p className="text-teal-700">{t('familyPortal.subtitle', "Stay connected with your loved one's care team")}</p>
            </div>
          </div>
          {notifications.filter(n => !n.read).length > 0 && (
            <Badge className="bg-red-500">
              {notifications.filter(n => !n.read).length} {t('familyPortal.newNotifications', 'new notifications')}
            </Badge>
          )}
        </div>

        {/* Alert Banner for Hotlist Patients */}
        {dashboard?.is_on_hotlist && (
          <Card className="mb-6 border-amber-300 bg-amber-50">
            <CardContent className="pt-6">
              <div className="flex items-start gap-4">
                <AlertTriangle className="w-8 h-8 text-amber-600 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-amber-800">{t('familyPortal.enhancedCare', 'Enhanced Comfort Care')}</h3>
                  <p className="text-amber-700 text-sm">
                    {t('familyPortal.enhancedCareDesc', 'Our care team is providing enhanced comfort-focused care. We are here to support both your loved one and your family during this time.')}
                  </p>
                  <p className="text-amber-600 text-sm mt-2">
                    {t('familyPortal.nurseLineLabel', '24/7 Nurse Line')}: <strong>703-578-7108</strong>
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Quick Actions */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Dialog open={showMessageDialog} onOpenChange={setShowMessageDialog}>
            <DialogTrigger asChild>
              <Card className="cursor-pointer hover:shadow-md transition-shadow bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
                <CardContent className="pt-6 text-center">
                  <MessageSquare className="w-8 h-8 mx-auto mb-2 text-blue-600" />
                  <p className="font-medium text-blue-800">{t('familyPortal.messageTeam', 'Message Team')}</p>
                </CardContent>
              </Card>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{t('familyPortal.sendMessage', 'Send Message to Care Team')}</DialogTitle>
                <DialogDescription>
                  {t('familyPortal.messageDesc', 'Your message will be sent to the entire care team')}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div>
                  <Label>{t('familyPortal.yourName', 'Your Name')}</Label>
                  <Input
                    value={newMessage.from_name}
                    onChange={(e) => setNewMessage({...newMessage, from_name: e.target.value})}
                    placeholder={t('familyPortal.yourNamePlaceholder', 'Your name')}
                  />
                </div>
                <div>
                  <Label>{t('familyPortal.relationship', 'Relationship')}</Label>
                  <Input
                    value={newMessage.from_relationship}
                    onChange={(e) => setNewMessage({...newMessage, from_relationship: e.target.value})}
                    placeholder={t('familyPortal.relationshipPlaceholder', 'e.g., Daughter, Son, Spouse')}
                  />
                </div>
                <div>
                  <Label>{t('messages.message', 'Message')}</Label>
                  <Textarea
                    value={newMessage.message}
                    onChange={(e) => setNewMessage({...newMessage, message: e.target.value})}
                    placeholder={t('messages.messagePlaceholder', 'Type your message...')}
                    rows={4}
                  />
                </div>
                <div className="flex items-center gap-2">
                  <input 
                    type="checkbox" 
                    id="urgent"
                    checked={newMessage.urgent}
                    onChange={(e) => setNewMessage({...newMessage, urgent: e.target.checked})}
                  />
                  <Label htmlFor="urgent">{t('familyPortal.markUrgent', 'Mark as urgent')}</Label>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowMessageDialog(false)}>{t('common.cancel', 'Cancel')}</Button>
                <Button onClick={sendMessage} disabled={loading}>
                  <Send className="w-4 h-4 mr-2" />
                  {t('messages.sendMessage', 'Send Message')}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <Card 
            className="cursor-pointer hover:shadow-md transition-shadow bg-gradient-to-br from-green-50 to-green-100 border-green-200"
            onClick={() => { setSelectedService("nurse_call"); requestService("nurse_call"); }}
          >
            <CardContent className="pt-6 text-center">
              <Phone className="w-8 h-8 mx-auto mb-2 text-green-600" />
              <p className="font-medium text-green-800">{t('familyPortal.callNurse', 'Call Nurse')}</p>
            </CardContent>
          </Card>

          <Card 
            className="cursor-pointer hover:shadow-md transition-shadow bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200"
            onClick={() => { setSelectedService("chaplain"); requestService("chaplain"); }}
          >
            <CardContent className="pt-6 text-center">
              <Cross className="w-8 h-8 mx-auto mb-2 text-purple-600" />
              <p className="font-medium text-purple-800">{t('familyPortal.requestChaplain', 'Request Chaplain')}</p>
            </CardContent>
          </Card>

          <Card 
            className="cursor-pointer hover:shadow-md transition-shadow bg-gradient-to-br from-teal-50 to-teal-100 border-teal-200"
            onClick={() => { setSelectedService("video_call"); requestService("video_call"); }}
          >
            <CardContent className="pt-6 text-center">
              <Video className="w-8 h-8 mx-auto mb-2 text-teal-600" />
              <p className="font-medium text-teal-800">{t('familyPortal.scheduleVideoCall', 'Schedule Video Call')}</p>
            </CardContent>
          </Card>
        </div>

        {/* Main Content */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-white border shadow-sm mb-6">
            <TabsTrigger value="overview" data-testid="overview-tab">{t('familyPortal.overview', 'Overview')}</TabsTrigger>
            <TabsTrigger value="updates" data-testid="updates-tab">
              {t('familyPortal.updates', 'Updates')}
              {updates.length > 0 && <Badge className="ml-2">{updates.length}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="resources" data-testid="resources-tab">{t('familyPortal.resources', 'Resources')}</TabsTrigger>
            <TabsTrigger value="timeline" data-testid="timeline-tab">{t('familyPortal.careTimeline', 'Care Timeline')}</TabsTrigger>
          </TabsList>

          <TabsContent value="overview">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Care Team */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="w-5 h-5 text-teal-600" />
                    {t('familyPortal.yourCareTeam', 'Your Care Team')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div>
                        <p className="font-medium">{t('familyPortal.primaryNurse', 'Primary Nurse')}</p>
                        <p className="text-sm text-slate-600">{dashboard?.care_team?.primary_nurse || t('familyPortal.primaryNurse', 'Assigned Nurse')}</p>
                      </div>
                      <Button variant="outline" size="sm">
                        <Phone className="w-4 h-4 mr-1" />
                        {t('familyPortal.contact', 'Contact')}
                      </Button>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div>
                        <p className="font-medium">{t('familyPortal.attendingPhysician', 'Attending Physician')}</p>
                        <p className="text-sm text-slate-600">{dashboard?.care_team?.attending_physician || "Dr. Attending"}</p>
                      </div>
                      <Button variant="outline" size="sm">
                        <MessageSquare className="w-4 h-4 mr-1" />
                        {t('familyPortal.message', 'Message')}
                      </Button>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div>
                        <p className="font-medium">{t('familyPortal.socialWorker', 'Social Worker')}</p>
                        <p className="text-sm text-slate-600">{t('familyPortal.supportServices', 'Support Services')}</p>
                      </div>
                      <Button variant="outline" size="sm" onClick={() => requestService("social_worker")}>
                        <Phone className="w-4 h-4 mr-1" />
                        {t('familyPortal.request', 'Request')}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Next Visit */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-teal-600" />
                    {t('familyPortal.upcomingVisits', 'Upcoming Visits')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {dashboard?.next_visit ? (
                    <div className="p-4 bg-teal-50 border border-teal-200 rounded-lg">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">{dashboard.next_visit.visit_type}</p>
                          <p className="text-sm text-teal-700">{dashboard.next_visit.scheduled_date}</p>
                        </div>
                        <Badge>{t('familyPortal.scheduled', 'Scheduled')}</Badge>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8 text-slate-500">
                      <Calendar className="w-12 h-12 mx-auto mb-2 opacity-50" />
                      <p>{t('familyPortal.visitScheduleAppear', 'Visit schedule will appear here')}</p>
                    </div>
                  )}

                  {dashboard?.care_plan_summary?.focus_areas?.length > 0 && (
                    <div className="mt-4 pt-4 border-t">
                      <p className="font-medium mb-2">{t('familyPortal.currentFocusAreas', 'Current Focus Areas')}</p>
                      <div className="flex flex-wrap gap-2">
                        {dashboard.care_plan_summary.focus_areas.map((area, idx) => (
                          <Badge key={idx} variant="outline">{area.replace('_', ' ')}</Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Recent Notifications */}
              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Bell className="w-5 h-5 text-teal-600" />
                    {t('familyPortal.recentNotifications', 'Recent Notifications')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {notifications.length === 0 ? (
                    <div className="text-center py-8 text-slate-500">
                      <Bell className="w-12 h-12 mx-auto mb-2 opacity-50" />
                      <p>{t('familyPortal.noNotifications', 'No notifications yet')}</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {notifications.slice(0, 5).map((notif, idx) => (
                        <div key={idx} className={`p-3 rounded-lg ${notif.read ? 'bg-slate-50' : 'bg-blue-50 border border-blue-200'}`}>
                          <div className="flex items-center justify-between">
                            <p className="font-medium">{notif.subject}</p>
                            <span className="text-xs text-slate-500">{new Date(notif.sent_at).toLocaleDateString()}</span>
                          </div>
                          <p className="text-sm text-slate-600 mt-1 line-clamp-2">{notif.message?.slice(0, 150)}...</p>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="updates">
            <Card>
              <CardHeader>
                <CardTitle>{t('familyPortal.careUpdates', 'Care Updates')}</CardTitle>
                <CardDescription>{t('familyPortal.recentUpdates', 'Recent updates from your care team')}</CardDescription>
              </CardHeader>
              <CardContent>
                {updates.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <FileText className="w-16 h-16 mx-auto mb-4 opacity-50" />
                    <p>{t('familyPortal.noUpdates', 'No updates yet')}</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {updates.map((update, idx) => (
                      <div key={idx} className={`p-4 border rounded-lg ${
                        update.priority === 'urgent' ? 'border-red-200 bg-red-50' :
                        update.priority === 'important' ? 'border-amber-200 bg-amber-50' :
                        'border-slate-200'
                      }`}>
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-medium">{update.title}</h4>
                          <span className="text-xs text-slate-500">{new Date(update.created_at).toLocaleDateString()}</span>
                        </div>
                        <p className="text-sm text-slate-700">{update.message}</p>
                        {update.from_staff && (
                          <p className="text-xs text-slate-500 mt-2">{t('familyPortal.from', 'From')}: {update.from_staff}</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="resources">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {Object.entries(resources).map(([category, items]) => (
                <Card key={category} className={getCategoryColor(category)}>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      {getCategoryIcon(category)}
                      {category.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {items.slice(0, 4).map((resource, idx) => (
                        <div key={idx} className="p-2 bg-white/50 rounded">
                          <p className="font-medium text-sm">{resource.title}</p>
                          <p className="text-xs opacity-75">{resource.description}</p>
                          {resource.downloadable && (
                            <Button variant="link" size="sm" className="p-0 h-auto mt-1">
                              <ExternalLink className="w-3 h-3 mr-1" />
                              {t('familyPortal.download', 'Download')}
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="timeline">
            <Card>
              <CardHeader>
                <CardTitle>{t('familyPortal.careTimeline', 'Care Timeline')}</CardTitle>
                <CardDescription>{t('familyPortal.careTimelineActivities', 'Recent care activities')}</CardDescription>
              </CardHeader>
              <CardContent>
                {timeline.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <Clock className="w-16 h-16 mx-auto mb-4 opacity-50" />
                    <p>{t('familyPortal.timelineAppear', 'Care timeline will appear here')}</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {timeline.map((item, idx) => (
                      <div key={idx} className="flex gap-4">
                        <div className="flex flex-col items-center">
                          <div className={`w-3 h-3 rounded-full ${
                            item.type === 'visit' ? 'bg-green-500' : 'bg-blue-500'
                          }`} />
                          {idx < timeline.length - 1 && <div className="w-px h-full bg-slate-200" />}
                        </div>
                        <div className="pb-4">
                          <p className="font-medium">{item.title}</p>
                          <p className="text-sm text-slate-600">{item.description}</p>
                          <p className="text-xs text-slate-400 mt-1">
                            {new Date(item.date).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Emergency Contact Footer */}
        <Card className="mt-8 bg-gradient-to-r from-red-50 to-red-100 border-red-200">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Phone className="w-8 h-8 text-red-600" />
                <div>
                  <p className="font-semibold text-red-800">{t('familyPortal.emergencyLine', '24/7 Emergency Line')}</p>
                  <p className="text-red-700">703-578-7108</p>
                </div>
              </div>
              <Button className="bg-red-600 hover:bg-red-700">
                <Phone className="w-4 h-4 mr-2" />
                {t('familyPortal.callNow', 'Call Now')}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
